import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams, useParams, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import ScoopPanel from '@/components/scoops/ScoopPanel';
import NotificationBell from '@/components/social/NotificationBell';
import GlobalSearch from '@/components/search/GlobalSearch';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  Home,
  Compass,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  Loader2,
  Mail,
  Shield,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  FolderPlus,
  Sparkles,
  Flame,
} from 'lucide-react';
import AIAssistant from '@/components/AIAssistant';
import { AIAssistantProvider, useAIAssistantContext } from '@/contexts/AIAssistantContext';
import { CommandPalette, CommandItem } from '@/components/CommandPalette';
import SaveToCollectionModal from '@/components/collections/SaveToCollectionModal';
import OnboardingTour from '@/components/OnboardingTour';
import { useOnboardingTour } from '@/hooks/useOnboardingTour';
import { RightSidebar } from '@/components/dashboard/RightSidebar';
import CollectionsSidebar from '@/components/library/CollectionsSidebar';
import { OnboardingFlow } from '@/components/onboarding';
import { WorkspaceSidebar } from '@/components/dashboard/WorkspaceSidebar';
import FullComposer from '@/components/feed/FullComposer';
import { DeviceProvider, useDeviceContext } from '@/contexts/DeviceContext';
import UsageAlertBanner from '@/components/dashboard/UsageAlert';
import Link from '@/components/LocaleLink';
import { useLocaleNavigate } from '@/hooks/useLocaleNavigate';
import FramedAvatar from '@/components/FramedAvatar';
import { useEquippedCosmetics } from '@/hooks/useEquippedCosmetics';
import { useContentLanguages } from '@/hooks/useContentLanguages';
import { clearToken, listSessions, setToken, removeSession } from '@/utils/secureToken';
import AccountSwitcher from '@/components/layout/AccountSwitcher';
import type { DashboardOutletContext } from './dashboardOutlet';
import { useDesignV2 } from '@/hooks/useDesignV2';
import DashboardSidebarV2 from '@/components/dashboard/DashboardSidebarV2';
import { buildDashboardNavGroups, buildPrimaryDashboardNav } from '@/components/dashboard/dashboardNavigation';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Library folder type
interface LibraryFolder {
  id: string;
  name: string;
  icon: string;
  isInbox: boolean;
  linkCount: number;
}

function parseJwt(token: string): { sub?: string; email?: string; role?: string; exp?: number } | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
  hideTitle?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

function AIAssistantButton({ drawer = false, onOpen }: { drawer?: boolean; onOpen?: () => void }) {
  const { setIsOpen, unreadCount } = useAIAssistantContext();
  const { t } = useTranslation();
  const label = t('chromeAI.aiAssistant', 'AI assistant');

  return (
    <button
      type="button"
      onClick={() => { onOpen?.(); setIsOpen(true); }}
      className={drawer
        ? 'w-full min-h-11 px-3 rounded-xl flex items-center gap-3 text-sm text-[#b8b8c8] hover:text-white hover:bg-white/6 transition-colors'
        : 'relative min-h-11 px-3 rounded-xl flex items-center gap-2 text-sm text-[#b8b8c8] hover:text-white hover:bg-white/6 transition-colors'}
      aria-label={label}
    >
      <Sparkles size={18} className="text-iris-400" />
      <span>{label}</span>
      {unreadCount > 0 && (
        <span className="min-w-4 h-4 px-1 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}

function DashboardLayoutInner() {
  const { t } = useTranslation();

  // Mark the document while the signed-in app is mounted so the consent
  // widget's floating button can be hidden here (see index.html).
  //
  // The button is the withdrawal path and GDPR Art. 7(3) requires withdrawal
  // to stay as easy as giving — but it does NOT require a floating button.
  // Inside the app that path lives in Settings → Privacy, which is where a
  // signed-in user looks for it anyway; a 44%-of-screen-width bubble hovering
  // over the product is the wrong way to satisfy the same rule.
  useEffect(() => {
    document.documentElement.classList.add('sk-in-app');
    return () => document.documentElement.classList.remove('sk-in-app');
  }, []);

  // Phase 11i — ensure every signed-in user has content-language prefs.
  // First-login users get seeded from their browser/UI locale so their
  // feed starts filtered out of the box; no-op for users who already
  // have prefs.
  useContentLanguages();

  // Use the same information architecture as V2's grouped More menu. The
  // legacy desktop rail and mobile drawer remain complete without reverting
  // to one unexplained, permanently-expanded icon list.
  const NAV_SECTIONS: NavSection[] = useMemo(() => [
    {
      id: 'primary',
      title: t('nav.social'),
      hideTitle: true,
      items: buildPrimaryDashboardNav(t).map(({ href, label, Icon }) => ({
        href,
        label,
        icon: <Icon size={20} />,
      })),
    },
    ...buildDashboardNavGroups(t).map(group => ({
      id: group.id,
      title: group.label,
      collapsible: true,
      defaultCollapsed: group.id !== 'content',
      items: group.items.map(({ href, label, Icon }) => ({
        href,
        label,
        icon: <Icon size={20} />,
      })),
    })),
  ], [t]);

  // Sidebar badges. These were hardcoded to 0, which is worse than showing
  // nothing: a zero badge asserts "nothing new" even when there is. Both
  // counts come from endpoints that already exist and are already cheap
  // (the notification count is server-cached for 15s).
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let alive = true;
    const headers = { Authorization: `Bearer ${token}` };
    const load = () => {
      fetch(`${API_URL}/messages/unread`, { headers })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (alive && typeof d?.unreadCount === 'number') setUnreadMessages(d.unreadCount); })
        .catch(() => {});
      fetch(`${API_URL}/social/notifications/count`, { headers })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (alive && typeof d?.count === 'number') setUnreadNotifications(d.count); })
        .catch(() => {});
    };
    load();
    // Slow poll — the badge should not be stale for a whole session, but it
    // is not worth a socket or a tight interval either. Opening a conversation
    // clears its unread server-side, so the messages page fires this event to
    // pull the badge down now instead of up to a minute later.
    const id = setInterval(load, 60_000);
    window.addEventListener('soltik-unread-changed', load);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener('soltik-unread-changed', load);
    };
  }, []);

  const navigate = useLocaleNavigate();
  const { pathname: rawPathname } = useLocation();
  const { locale } = useParams();
  // Strip the /:locale prefix so active-state comparisons match the
  // nav hrefs (which are written without the locale). Without this,
  // pathname="/en/dashboard/create" never matched href="/dashboard/create"
  // and nothing in the sidebar highlighted.
  const pathname = locale && rawPathname.startsWith(`/${locale}/`)
    ? rawPathname.slice(`/${locale}`.length)
    : (locale && rawPathname === `/${locale}` ? '/' : rawPathname);

  // Hide dashboard sidebar when inside shop management — ShopLayout has its own sidebar
  const isShopManagement = /\/dashboard\/commerce\/[^/]+\/.+/.test(pathname) || /\/dashboard\/commerce\/[^/]+$/.test(pathname);
  // Auto-collapse main sidebar when inside a hub detail page (hub has its own ChannelSidebar)
  const isInsideHub = /\/dashboard\/scoops\/[^/]+/.test(pathname) && pathname !== '/dashboard/scoops';
  // Studio has its own left rail (5 page tabs) — showing the dashboard
  // sidebar at full width alongside it produces a two-menu look. Collapse
  // the dashboard sidebar to the 16px icon strip the moment a user enters
  // Studio so the content area is the focus.
  const isInsideStudio = /\/dashboard\/studio(\/|$|\?)/.test(pathname);
  const [searchParams] = useSearchParams();

  // Detect scoop panel from query param or route pattern
  const scoopIdFromQuery = searchParams.get('scoop');
  // Scoop overlay — triggered by query param ?scoop=:id (not route-based)
  const scoopRouteMatch: RegExpMatchArray | null = null;
  const activeScoopId = scoopIdFromQuery || scoopRouteMatch?.[1] || null;

  const handleCloseScoopPanel = useCallback(() => {
    if (scoopIdFromQuery) {
      // Remove the scoop query param
      const params = new URLSearchParams(searchParams.toString());
      params.delete('scoop');
      const newQuery = params.toString();
      navigate(newQuery ? `${pathname}?${newQuery}` : pathname, { replace: true, preventScrollReset: true });
    } else if (scoopRouteMatch) {
      // Navigate back from route-based panel
      navigate(-1);
    }
  }, [scoopIdFromQuery, scoopRouteMatch, pathname, searchParams, navigate]);

  const [user, setUser] = useState<{ email: string; id: string; username?: string | null; role?: string; avatar_url?: string | null; display_name?: string | null; current_streak?: number } | null>(null);
  // Pages read the profile from here (useDashboardUser) instead of refetching it.
  // Memoised so a layout re-render does not re-render every consumer.
  const outletContext = useMemo<DashboardOutletContext>(() => ({ user }), [user]);
  const equipped = useEquippedCosmetics();
  const designV2 = useDesignV2();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Always close the mobile nav drawer on a route change — covers
  // programmatic navigation, back/forward, and any link that doesn't
  // self-close. Without this the drawer can linger over the new page.
  useEffect(() => { setSidebarOpen(false); }, [pathname]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dashboard_sidebar_collapsed') === 'true';
    }
    return false;
  });
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  // Assets handed in when the composer is opened from the media library
  // ("Post to feed"): the full composer opens pre-loaded with them.
  const [composeInitialAssets, setComposeInitialAssets] = useState<any[] | undefined>(undefined);

  // Sidebar V2 "+" button dispatches a window CustomEvent so the FullComposer
  // modal opens over whatever page the user is reading. Not a URL param: the
  // user can open → close → reopen without the param getting "stuck" consumed.
  // detail.assets (optional) pre-loads library media into the composer.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onCompose = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setComposeInitialAssets(Array.isArray(detail?.assets) ? detail.assets : undefined);
      setShowCompose(true);
    };
    window.addEventListener('soltik:compose', onCompose);
    return () => window.removeEventListener('soltik:compose', onCompose);
  }, []);

  // Deep-link support: external producers (Studio publish flow, emails, push
  // notifications) navigate to `/dashboard?compose=1` to pop the modal. We
  // consume + strip the param via replaceState so a back-button doesn't
  // re-trigger and so close → reopen via the sidebar still works.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (searchParams.get('compose') !== '1') return;
    setShowCompose(true);
    const url = new URL(window.location.href);
    url.searchParams.delete('compose');
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  }, [searchParams]);
  const [showSaveToCollection, setShowSaveToCollection] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      NAV_SECTIONS
        .filter(section => section.defaultCollapsed)
        .map(section => [section.id, true]),
    ),
  );

  // Device sync state
  const {
    devices,
    selectedDevice,
    setSelectedDevice,
    isLoading: devicesLoading,
    fetchDevices,
    deleteDevice,
  } = useDeviceContext();

  // Workspace stats
  const [workspaceStats, setWorkspaceStats] = useState<{
    openTabs?: number;
    savedTabs?: number;
    historyItems?: number;
    bookmarks?: number;
  }>({});

  // Fetch workspace stats
  const fetchWorkspaceStats = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/workspace/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.stats) {
          setWorkspaceStats(data.stats);
        }
      }
    } catch (e) {
      console.error('Failed to fetch workspace stats:', e);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaceStats();
  }, [fetchWorkspaceStats]);

  // Library folders
  const [libraryFolders, setLibraryFolders] = useState<LibraryFolder[]>([]);

  const fetchLibraryFolders = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      // Ensure inbox exists
      await fetch(`${API_URL}/library/folders/ensure-inbox`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const res = await fetch(`${API_URL}/library/folders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLibraryFolders(data.folders || data.collections || []);
      }
    } catch (e) {
      console.error('Failed to fetch library folders:', e);
    }
  }, []);

  useEffect(() => {
    fetchLibraryFolders();
  }, [fetchLibraryFolders]);

  // Onboarding tour
  const {
    isCompleted: isTourCompleted,
    isTourActive,
    currentStep,
    totalSteps,
    startTour,
    completeTour,
    nextStep,
    prevStep,
    skipTour,
    goToStep,
  } = useOnboardingTour();
  const [impersonating, setImpersonating] = useState(false);
  const [showUsernamePrompt, setShowUsernamePrompt] = useState(false);
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<{ checking: boolean; available?: boolean; error?: string }>({ checking: false });
  const [savingUsername, setSavingUsername] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    const payload = parseJwt(token);
    if (!payload?.email || !payload?.sub) {
      navigate('/login', { replace: true });
      return;
    }
    // Check if token is expired
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('authToken');
      window.dispatchEvent(new CustomEvent('soltik-web-auth-change', { detail: {} }));
      navigate('/login?expired=1', { replace: true });
      return;
    }
    // The banner must track the ACTIVE session, not just the presence of a
    // stashed admin token. The impersonated account also lands in the account
    // switcher, so an admin who hops back to themselves that way (instead of
    // via "Return to admin") would otherwise keep seeing "Viewing as" while
    // signed in as themselves.
    const stashedAdmin = localStorage.getItem('adminAuthToken');
    let impersonatedId: string | undefined;
    try { impersonatedId = JSON.parse(localStorage.getItem('impersonation') || '{}')?.userId; } catch { /* no record */ }
    setImpersonating(!!stashedAdmin && (!impersonatedId || payload.sub === impersonatedId));

    // Validate token with API before rendering dashboard
    fetch(`${API_URL}/user/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        if (r.status === 401) {
          localStorage.removeItem('authToken');
          window.dispatchEvent(new CustomEvent('soltik-web-auth-change', { detail: {} }));
          navigate('/login?expired=1', { replace: true });
          return null;
        }
        // Banned accounts get a structured 403; bounce to the appeal
        // page rather than showing a generic error.
        if (r.status === 403) {
          try {
            const body = await r.clone().json();
            if (body?.code === 'banned') {
              navigate('/banned', { replace: true });
              return null;
            }
          } catch { /* fall through */ }
        }
        return r.ok ? r.json() : null;
      })
      .then(data => {
        if (!data) return;
        // Token is valid — now set user
        setUser({ email: payload.email || '', id: payload.sub || '', role: payload.role, username: data.username, avatar_url: data.avatar_url, display_name: data.display_name, current_streak: data.current_streak });
        // Stitch anonymous PostHog events to the real user so funnels work.
        import('@/lib/posthog').then(({ identifyPostHog }) => identifyPostHog({
          id: payload.sub || '',
          email: payload.email,
          username: data.username,
          display_name: data.display_name,
        })).catch(() => {});
        // isDeveloper is always true — all users can access developer portal
        // Check if onboarding was already dismissed this session
        const onboardingDismissed = sessionStorage.getItem('soltik_onboarding_done');

        if (!onboardingDismissed) {
          const hasUsername = !!data.username;
          const hasDisplayName = !!data.display_name && data.display_name.trim().length > 0;
          const hasBirthday = !!data.birthday;
          const profileComplete = hasUsername && hasDisplayName && hasBirthday;

          if (!profileComplete) {
            setNeedsProfile(true);
            setShowOnboardingFlow(true);
          } else if (!data.onboarding_completed_at) {
            setShowOnboardingFlow(true);
          }
        }
      })
      .catch(() => {});
  }, [navigate]);

  // Username availability check with debounce
  useEffect(() => {
    if (!usernameInput || usernameInput.length < 2) {
      setUsernameStatus({ checking: false });
      return;
    }
    setUsernameStatus({ checking: true });
    const timeout = setTimeout(() => {
      const token = localStorage.getItem('authToken');
      fetch(`${API_URL}/user/username/check?username=${encodeURIComponent(usernameInput)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.json())
        .then(data => setUsernameStatus({ checking: false, available: data.available, error: data.reason }))
        .catch(() => setUsernameStatus({ checking: false }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [usernameInput]);

  // Tour auto-start disabled — onboarding flow handles everything

  const handleSetUsername = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !usernameInput.trim()) return;
    setSavingUsername(true);
    try {
      const res = await fetch(`${API_URL}/user/username`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usernameInput.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setUser(prev => prev ? { ...prev, username: data.username } : prev);
        setShowUsernamePrompt(false);
        // Start onboarding flow after username is set
        setShowOnboardingFlow(true);
      } else {
        setUsernameStatus({ checking: false, available: false, error: data.error });
      }
    } catch {} finally { setSavingUsername(false); }
  };

  // Cmd+K command palette, Cmd+Shift+S save to collection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        setShowSaveToCollection(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cmd+K is the searchable counterpart to More. Keep it derived from the
  // same sections so newly-added destinations cannot silently disappear.
  const searchableNavItems = Array.from(new Map(
    NAV_SECTIONS.flatMap(section => section.items).map(item => [item.href, item]),
  ).values());
  const commandItems: CommandItem[] = [
    ...searchableNavItems.map(item => ({
      id: `nav-${item.href}`,
      label: item.label,
      icon: item.icon,
      action: () => { navigate(item.href); setCommandPaletteOpen(false); },
      category: 'navigation' as const,
    })),
    { id: 'save-to-collection', label: 'Save to Collection', icon: <FolderPlus size={16} />, action: () => { setShowSaveToCollection(true); setCommandPaletteOpen(false); }, category: 'action', shortcut: '⌘⇧S' },
  ];

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = Object.fromEntries(
        NAV_SECTIONS
          .filter(section => section.collapsible)
          .map(section => [section.id, true]),
      );
      if (prev[sectionId]) next[sectionId] = false;
      return next;
    });
  };

  const handleLogout = () => {
    // Multi-account: clearToken() removes the active session and promotes
    // the next one if any exist. Only hard-redirect to /login when the
    // device has no sessions left.
    clearToken();
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('impersonation');
    const next = listSessions()[0];
    // Include the promoted session's token in the event so the extension
    // sync doesn't echo back a logout and clobber localStorage.authToken.
    window.dispatchEvent(new CustomEvent('soltik-web-auth-change', {
      detail: next ? { token: next.token, email: next.email || '' } : {},
    }));
    window.location.href = next ? '/en/dashboard' : '/en/login';
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('dashboard_sidebar_collapsed', String(next));
      return next;
    });
  };

  // Effective collapsed state: user pref OR auto-collapse inside hubs OR Studio.
  // Studio has its own left rail so we always collapse the dashboard sidebar
  // to avoid the two-menu look.
  const effectiveCollapsed = sidebarCollapsed || isInsideHub || isInsideStudio;

  const isActive = (href: string) => {
    if (href === '/dashboard') return pathname === '/dashboard';
    // /dashboard/chats is the canonical URL but /dashboard/messages
    // is a route alias (existing links/bookmarks). Either path lights
    // up the Chats nav entry.
    if (href === '/dashboard/chats' && (pathname === '/dashboard/messages' || pathname.startsWith('/dashboard/messages/'))) {
      return true;
    }
    const [hrefPath, hrefQuery] = href.split('?');
    if (hrefQuery) {
      const hrefType = new URLSearchParams(hrefQuery).get('type');
      const currentType = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('type')
        : null;
      return pathname === hrefPath && hrefType === currentType;
    }
    if (hrefPath === '/dashboard/library') {
      const hasTypeParam = typeof window !== 'undefined' && window.location.search.includes('type=');
      return pathname === '/dashboard/library' && !hasTypeParam;
    }
    if (pathname === hrefPath) return true;
    if (!pathname.startsWith(hrefPath + '/')) return false;

    // Let a configured child own its route, but keep the parent active for
    // unlisted detail/sub-settings routes. This also reveals the correct
    // section when the mobile drawer opens on a deep link.
    const hasMoreSpecificMatch = NAV_SECTIONS
      .flatMap(section => section.items)
      .some(item => {
        const itemPath = item.href.split('?')[0];
        return itemPath !== hrefPath
          && itemPath.startsWith(hrefPath + '/')
          && (pathname === itemPath || pathname.startsWith(itemPath + '/'));
      });
    return !hasMoreSpecificMatch;
  };

  if (!user) return null;

  // Tooltip wrapper for collapsed sidebar items
  const SidebarTooltip = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="relative group/tip">
      {children}
      <div className="absolute top-1/2 -translate-y-1/2 ltr:left-full rtl:right-full ltr:ml-2 rtl:mr-2 px-2 py-1 text-xs text-white bg-[#16161e] border border-white/10 rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover/tip:opacity-100 transition-opacity z-50">
        {label}
      </div>
    </div>
  );

  const sidebarContent = (collapsed: boolean) => (
    <>
      {/* Profile card header */}
      <div className={`relative border-b border-white/5 ${collapsed ? 'px-2 py-3' : 'px-3 py-3'}`}>
        <div className={`flex items-center ${collapsed ? 'flex-col gap-2' : 'gap-3'}`}>
          {collapsed ? (
            <SidebarTooltip label={user?.display_name || user?.username || user?.email?.split('@')[0] || ''}>
              <Link to="/dashboard/settings" className="shrink-0">
                <FramedAvatar
                  userId={user?.id || null}
                  avatarUrl={user?.avatar_url}
                  name={user?.display_name || user?.username || user?.email}
                  equippedFrameSlug={equipped.profile_frame?.slug}
                  size={40}
                />
              </Link>
            </SidebarTooltip>
          ) : (
            <Link to="/dashboard/settings" className="shrink-0">
              <FramedAvatar
                userId={user?.id || null}
                avatarUrl={user?.avatar_url}
                name={user?.display_name || user?.username || user?.email}
                equippedFrameSlug={equipped.profile_frame?.slug}
                size={40}
              />
            </Link>
          )}
          {collapsed && (
            <button
              onClick={toggleSidebarCollapse}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/5 transition-all"
              title={t('nav.expandSidebar')}
            >
              <PanelLeftOpen size={16} />
            </button>
          )}
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.display_name || user?.username || user?.email?.split('@')[0] || ''}
              </p>
              {user?.username && (
                <p className="text-xs text-[#9a9aac] truncate">@{user.username}</p>
              )}
            </div>
          )}
          {!collapsed && <AccountSwitcher />}
          {!collapsed && (
            <button
              onClick={toggleSidebarCollapse}
              className="hidden lg:flex items-center justify-center w-7 h-7 rounded-md text-[#9a9aac] hover:text-white hover:bg-white/5 transition-all shrink-0"
              title={t('nav.collapseSidebar')}
            >
              <PanelLeftClose size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Streak indicator */}
      {(user?.current_streak ?? 0) > 0 && (
        <div className={`${collapsed ? 'px-1.5' : 'px-2'} py-1`}>
          {collapsed ? (
            <SidebarTooltip label={`${user!.current_streak} day streak`}>
              <div className="flex items-center justify-center w-9 h-9 mx-auto rounded-lg bg-orange-500/10">
                <Flame size={16} className="text-orange-400" />
              </div>
            </SidebarTooltip>
          ) : (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <Flame size={14} className="text-orange-400" />
              <span className="text-sm font-semibold text-orange-300">{user!.current_streak}</span>
              <span className="text-xs text-[#a0a0b0]">{t('streak.dayStreak', 'day streak')}</span>
            </div>
          )}
        </div>
      )}

      {/* New Post CTA */}
      {collapsed ? (
        <div className="px-1.5 py-1.5">
          <button onClick={() => { setSidebarOpen(false); setShowCompose(true); }} className="flex items-center justify-center w-9 h-9 mx-auto rounded-lg bg-white/10 text-white hover:bg-white/15 transition-colors">
            <Plus size={16} />
          </button>
        </div>
      ) : (
        <div className="px-2 py-1.5">
          <button onClick={() => { setSidebarOpen(false); setShowCompose(true); }} className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-white/10 text-white/80 text-[13px] font-medium hover:bg-white/15 transition-colors">
            <Plus size={14} />
            {t('nav.newPost')}
          </button>
        </div>
      )}

      {/* Nav sections */}
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-1 ${collapsed ? 'px-1.5' : 'px-2'}`}>
        {NAV_SECTIONS.map((section, sectionIdx) => {
          // A collapsed desktop rail has no room for section controls, so keep
          // every destination available as a tooltip-backed icon. In the full
          // rail/mobile drawer, always reveal the group that owns the current
          // route so deep links do not hide their active destination.
          const containsActivePage = section.items.some(item => isActive(item.href));
          const isSectionCollapsed = !collapsed
            && section.collapsible
            && collapsedSections[section.id]
            && !containsActivePage;

          return (
            <div key={section.id}>
              {sectionIdx > 0 && <div className={`my-1.5 border-t border-white/5 ${collapsed ? 'mx-1' : 'mx-2'}`} />}
              {!collapsed && section.collapsible && (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex items-center justify-between w-full min-h-11 px-2 mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#5a5a6e] hover:text-[#8e8ea0] transition-colors"
                >
                  <span>{section.title}</span>
                  {isSectionCollapsed
                    ? <ChevronRight size={12} className="rtl:rotate-180" />
                    : <ChevronDown size={12} />}
                </button>
              )}
              {!collapsed && !section.collapsible && !section.hideTitle && (
                <div className="px-2 mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#5a5a6e]">
                  {section.title}
                </div>
              )}

              {!isSectionCollapsed && (
                <div className={collapsed ? 'space-y-0.5' : 'space-y-px'}>
                  {/* Library folders (dynamic) - show before static items */}
                  {section.title === t('nav.library') && libraryFolders.length > 0 && (
                    <>
                      {libraryFolders.map((folder) =>
                        collapsed ? (
                          <SidebarTooltip key={folder.id} label={folder.name}>
                            <Link
                              to={`/dashboard/library?folder=${folder.id}`}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center justify-center w-11 h-11 mx-auto rounded-xl transition-all ${
                                typeof window !== 'undefined' && window.location.search.includes(`folder=${folder.id}`)
                                  ? 'bg-brand-primary/15 text-brand-primary ring-1 ring-brand-primary/30'
                                  : 'text-[#a0a0b0] hover:text-white hover:bg-white/5'
                              }`}
                            >
                              <span className="text-sm">{folder.isInbox ? '\u{1F4E5}' : folder.icon}</span>
                            </Link>
                          </SidebarTooltip>
                        ) : (
                          <Link
                            key={folder.id}
                            to={`/dashboard/library?folder=${folder.id}`}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-2.5 min-h-11 px-2.5 py-2 rounded-xl text-[13px] transition-all ${
                              typeof window !== 'undefined' && window.location.search.includes(`folder=${folder.id}`)
                                ? 'bg-brand-primary/15 text-brand-primary font-semibold border-l-2 border-brand-primary ltr:pl-2 rtl:pr-2 ltr:border-l-2 rtl:border-l-0 rtl:border-r-2 shadow-xs shadow-brand-primary/10'
                                : 'text-[#a0a0b0] hover:text-white hover:bg-white/5'
                            }`}
                          >
                            <span className="text-sm leading-none">{folder.isInbox ? '\u{1F4E5}' : folder.icon}</span>
                            <span className="flex-1 truncate">{folder.name}</span>
                            {folder.linkCount > 0 && (
                              <span className="text-[11px] text-[#9a9aac]">{folder.linkCount}</span>
                            )}
                          </Link>
                        )
                      )}
                      {!collapsed && (
                        <div className="my-1 mx-2 border-t border-white/5" />
                      )}
                    </>
                  )}

                  {section.items.map((item) =>
                    collapsed ? (
                      <SidebarTooltip key={item.href} label={item.label}>
                        <Link
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center justify-center w-11 h-11 mx-auto rounded-xl transition-all ${
                            isActive(item.href)
                              ? 'bg-brand-primary/15 text-brand-primary ring-1 ring-brand-primary/35 shadow-[0_0_20px_rgba(228,230,58,0.14)]'
                              : 'text-[#b8b8c8] hover:text-white hover:bg-white/4'
                          }`}
                        >
                          {item.icon}
                        </Link>
                      </SidebarTooltip>
                    ) : (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-2.5 min-h-11 px-2.5 py-2 rounded-xl text-[13px] transition-all ${
                          isActive(item.href)
                            ? 'bg-brand-primary/14 text-brand-primary font-semibold border-l-2 border-brand-primary ltr:pl-2 rtl:pr-2 ltr:border-l-2 rtl:border-l-0 rtl:border-r-2 shadow-[0_0_20px_rgba(228,230,58,0.12)]'
                            : 'text-[#b8b8c8] hover:text-white hover:bg-white/4'
                        }`}
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Role-specific destinations stay separate from the shared navigation. */}
      <div className={`py-2 border-t border-white/5 space-y-px ${collapsed ? 'px-1.5' : 'px-2'}`}>
        {user?.role === 'superadmin' && (
          collapsed ? (
            <SidebarTooltip label={t('nav.adminPanel')}>
              <Link
                to="/mgt"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center justify-center w-10 h-10 mx-auto rounded-xl transition-all ${
                  pathname?.startsWith('/mgt')
                    ? 'bg-brand-primary/15 text-brand-primary ring-1 ring-brand-primary/30'
                    : 'text-[#9a9aac] hover:text-white hover:bg-white/5'
                }`}
              >
                <Shield size={20} />
              </Link>
            </SidebarTooltip>
          ) : (
            <Link
              to="/mgt"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-[13px] transition-all ${
                pathname?.startsWith('/mgt')
                  ? 'bg-brand-primary/15 text-brand-primary font-semibold border-l-2 border-brand-primary ltr:pl-2 rtl:pr-2 ltr:border-l-2 rtl:border-l-0 rtl:border-r-2 shadow-xs shadow-brand-primary/10'
                  : 'text-[#9a9aac] hover:text-white hover:bg-white/5'
              }`}
            >
              <Shield size={20} />
              {t('nav.adminPanel')}
            </Link>
          )
        )}
      </div>

      {/* User section — logout only (profile card is at top) */}
      <div className={`py-2 border-t border-white/5 ${collapsed ? 'px-1.5' : 'px-2'}`}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-1">
            <SidebarTooltip label={t('nav.expandSidebar')}>
              <button
                onClick={toggleSidebarCollapse}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/5 transition-all"
              >
                <PanelLeftOpen size={16} />
              </button>
            </SidebarTooltip>
            <SidebarTooltip label={t('common.signOut')}>
              <button
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-[#9a9aac] hover:text-red-400 hover:bg-red-500/10 transition-all"
              >
                <LogOut size={14} />
              </button>
            </SidebarTooltip>
          </div>
        ) : (
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-2.5 py-1.5 w-full rounded-lg text-[13px] text-[#9a9aac] hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut size={16} />
            {t('common.signOut')}
          </button>
        )}
      </div>
    </>
  );

  return (
    <AIAssistantProvider>
    <div className="min-h-screen bg-[#0a0a0f] flex overflow-x-clip relative">
      {/* Subtle agentic glow — same palette as the marketing landing
          page so logged-in users get a continuous brand experience. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] bg-iris-500/4 blur-[120px] rounded-full" />
        <div className="absolute top-[40%] -right-32 w-[600px] h-[500px] bg-brand-primary/4 blur-[120px] rounded-full" />
      </div>
      {/* Desktop Sidebar — hidden when inside shop management (ShopLayout has its own).
          Behind the designV2 flag (?v2=1), renders the icon-only content-first
          rail. Otherwise the existing expand/collapse sidebar. */}
      {!isShopManagement && designV2 && (
        <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 ltr:left-0 rtl:right-0 z-30">
          <DashboardSidebarV2
            user={user}
            unreadMessages={unreadMessages}
            unreadNotifications={unreadNotifications}
            compact={isInsideHub || isInsideStudio}
            onLogout={handleLogout}
          />
        </aside>
      )}
      {!isShopManagement && !designV2 && (
        <aside className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 ltr:left-0 rtl:right-0 bg-[#0a0a12] ltr:border-r rtl:border-l border-white/6 transition-all duration-300 ease-out overflow-x-hidden z-30 ${effectiveCollapsed ? 'lg:w-16' : 'lg:w-60'}`}>
          {sidebarContent(effectiveCollapsed)}
        </aside>
      )}

      {/* Mobile header — hidden when inside shop management (ShopLayout has its own) */}
      {!isShopManagement && (
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0a0a12] border-b border-white/5">
          <div className="flex items-center justify-between px-4 py-3">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo-yellow.png" alt="Soltik" className="w-7 h-7" />
              <span className="text-[19px] font-extrabold text-white" style={{ fontFamily: "'Inter', 'SF Pro Display', system-ui, -apple-system, sans-serif", letterSpacing: '-0.03em' }}>
                Solt<span className="text-brand-primary">ik</span>
              </span>
            </Link>
            <div className="flex items-center gap-1">
              <NotificationBell />
            </div>
          </div>
        </div>
      )}

      {/* Mobile sidebar drawer — hidden when inside shop management.
          The drawer is ALWAYS mounted and slides in via transform, so
          tapping the hamburger is instant instead of paying the heavy
          sidebarContent mount cost on open (was: "slow to no response"). */}
      {!isShopManagement && (
        <>
          {sidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 z-40 bg-black/70"
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <aside
            aria-hidden={!sidebarOpen}
            className={`lg:hidden fixed inset-y-0 ltr:left-0 rtl:right-0 z-60 w-64 max-w-[85vw] bg-[#0a0a12] ltr:border-r rtl:border-l border-white/5 flex flex-col overflow-y-auto overscroll-contain transition-transform duration-200 ease-out ${sidebarOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full pointer-events-none'}`}
          >
            <div className="flex justify-end px-2 pt-2">
              <button type="button" onClick={() => setSidebarOpen(false)} className="w-11 h-11 rounded-xl flex items-center justify-center text-[#9a9aac] hover:text-white hover:bg-white/6" aria-label={t('common.close', 'Close')}>
                <X size={20} />
              </button>
            </div>
            <div className="px-2 pb-2 border-b border-white/5">
              <AIAssistantButton drawer onOpen={() => setSidebarOpen(false)} />
            </div>
            {sidebarContent(false)}
          </aside>
        </>
      )}

      {/* Main content */}
      <main className={`flex-1 min-w-0 overflow-x-clip transition-all duration-200 ${isShopManagement ? '' : designV2 ? (isInsideHub || isInsideStudio ? 'lg:ltr:ml-16 lg:rtl:mr-16' : 'lg:ltr:ml-52 lg:rtl:mr-52') : effectiveCollapsed ? 'lg:ltr:ml-16 lg:rtl:mr-16' : 'lg:ltr:ml-60 lg:rtl:mr-60'}`}>
        <div className={`${isShopManagement ? 'pt-0' : 'pt-14 pb-[calc(4rem+env(safe-area-inset-bottom))]'} lg:pt-0 lg:pb-0`}>
          {impersonating && (
            <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between">
              <span className="text-sm text-amber-300">
                {t('common.viewingAs')} <strong>{user?.email}</strong>
              </span>
              <button
                onClick={() => {
                  const adminToken = localStorage.getItem('adminAuthToken');
                  if (!adminToken) return;
                  // Same reason as the entry path in mgt/Users: `authToken` is
                  // a mirror of the active multi-session entry, so restoring
                  // the admin means making their session active again — a raw
                  // write here would be reconciled straight back to the
                  // impersonated user on the next getToken().
                  let impersonated: { userId?: string; preexisting?: boolean } = {};
                  try { impersonated = JSON.parse(localStorage.getItem('impersonation') || '{}'); } catch { /* no record — skip cleanup */ }
                  // Drop the impersonated account from the switcher, but only
                  // if impersonation is what put it there. If the admin was
                  // genuinely signed in as that user too, this would sign them
                  // out of their own second account.
                  if (impersonated.userId && !impersonated.preexisting) removeSession(impersonated.userId);
                  setToken(adminToken);
                  localStorage.removeItem('adminAuthToken');
                  localStorage.removeItem('impersonation');
                  window.location.href = '/en/mgt';
                }}
                className="px-3 py-1 text-xs font-medium text-amber-200 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-md transition-colors"
              >
                {t('common.returnToAdmin')}
              </button>
            </div>
          )}
          {/* Usage alerts */}
          <UsageAlertBanner />
          {/* Desktop top bar — hidden when inside shop management */}
          <div className={`${isShopManagement ? 'hidden' : 'hidden lg:flex'} items-center justify-end gap-2 px-6 py-2 border-b border-white/5`}>
            <AIAssistantButton />
            <GlobalSearch />
            <LanguageSwitcher compact dropUp={false} />
          </div>
          {/* Library + Studio benefit from full viewport width (folder
              rail + asset grid + detail rail). Content pages (feed,
              article, etc.) keep the 1800px reading cap so they stay
              comfortable on ultrawide. The cap is the default; specific
              routes opt out. */}
          <div className={`flex mx-auto overflow-x-clip ${
            /\/(media|studio)(\b|\/)/.test(pathname) ? 'w-full max-w-none' : 'max-w-[1800px]'
          }`}>
            {/* overflow-x-clip (not -hidden): -hidden forces overflow-y to
                'auto', making these wrappers phantom scroll containers with
                no bounded height that never scroll — which broke the
                library's sticky detail rail (it stuck to a non-scrolling
                ancestor instead of the page). clip clips horizontally
                without creating a scroll container. */}
            <div className="flex-1 min-w-0 overflow-x-clip">
              <PageTransition key={pathname}>
                <Outlet context={outletContext} />
              </PageTransition>
            </div>
            {/* Contextual right sidebar based on page */}
            {pathname.includes('/bookmarks') || pathname.includes('/workspace') || pathname.includes('/write') || pathname.includes('/scoops/') || pathname.includes('/commerce') || pathname.includes('/marketplace') || pathname.includes('/wishlist') || pathname.includes('/go-live') || pathname.includes('/live-streams') || pathname.includes('/friends') || pathname.includes('/following') || pathname.includes('/pricing') || pathname.includes('/business') || pathname.includes('/my-orders') || pathname.includes('/stream') || pathname.includes('/my-articles') || pathname.includes('/collections/') || pathname.includes('/developer') || pathname.includes('/settings') || pathname.includes('/analytics') || pathname.includes('/visitor-analytics') || pathname.includes('/affiliates') || pathname.includes('/templates') || pathname.includes('/my-templates') || pathname.includes('/mgt') || pathname.includes('/ai-builder') || pathname.includes('/creative-studio') || pathname.includes('/teaching') || pathname.includes('/discover') || pathname.includes('/messages') || pathname.includes('/chats') || pathname === '/dashboard/create' || pathname === '/dashboard/commerce-hub' || pathname === '/dashboard/my-library' || pathname === '/dashboard/agent' || pathname === '/dashboard/media' || pathname.startsWith('/dashboard/media/') || (pathname.includes('/live') && !pathname.includes('/live-streams')) ? (
              // Pages with built-in sidebars or no need for right sidebar
              null
            ) : pathname.includes('/tabs') || pathname.includes('/history') ? (
              <div className="py-6 ltr:pr-6 rtl:pl-6 hidden xl:block">
                <WorkspaceSidebar
                  devices={devices}
                  selectedDevice={selectedDevice}
                  onSelectDevice={setSelectedDevice}
                  onAddDevice={() => window.open('https://chromewebstore.google.com/detail/soltik/cjfddifkbbaaglfbnodmajjdhakfgjoe', '_blank')}
                  onDeleteDevice={deleteDevice}
                  onRefresh={() => { fetchDevices(); fetchWorkspaceStats(); }}
                  isLoading={devicesLoading}
                  stats={workspaceStats}
                  currentPage={pathname.includes('/tabs') ? 'tabs' : 'history'}
                />
              </div>
            ) : pathname.includes('/library') ? (
              <div className="py-6 ltr:pr-6 rtl:pl-6 hidden xl:block">
                <CollectionsSidebar
                  selectedCollection={searchParams.get('folder')}
                  selectedType={searchParams.get('type')}
                  selectedTags={searchParams.get('tags')?.split(',').filter(Boolean) || []}
                  onCollectionSelect={(id) => {
                    const params = new URLSearchParams(searchParams.toString());
                    if (id) { params.set('folder', id); } else { params.delete('folder'); }
                    params.delete('type');
                    navigate(`/dashboard/library?${params.toString()}`);
                  }}
                  onTypeSelect={(type) => {
                    const params = new URLSearchParams(searchParams.toString());
                    if (type) { params.set('type', type); } else { params.delete('type'); }
                    params.delete('folder');
                    navigate(`/dashboard/library?${params.toString()}`);
                  }}
                  onTagSelect={(tag) => {
                    const params = new URLSearchParams(searchParams.toString());
                    const current = params.get('tags')?.split(',').filter(Boolean) || [];
                    const updated = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
                    if (updated.length > 0) { params.set('tags', updated.join(',')); } else { params.delete('tags'); }
                    navigate(`/dashboard/library?${params.toString()}`);
                  }}
                />
              </div>
            ) : (
              <div className="py-6 ltr:pr-6 rtl:pl-6 hidden xl:block">
                <RightSidebar />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile: familiar five-action social navigation. Everything else
          remains available in the labelled More drawer. */}
      {!isShopManagement && (
        <nav className="lg:hidden fixed inset-x-0 bottom-0 z-50 min-h-16 border-t border-white/8 bg-[#0a0a12]/95 backdrop-blur-xl px-2 pb-[env(safe-area-inset-bottom)] flex items-stretch justify-around" aria-label={t('nav.primary', 'Primary navigation')}>
          {[
            { href: '/dashboard', label: t('nav.home', 'Home'), icon: <Home size={20} /> },
            { href: '/dashboard/discover', label: t('nav.discover', 'Discover'), icon: <Compass size={20} /> },
          ].map(item => (
            <Link key={item.href} to={item.href} aria-current={isActive(item.href) ? 'page' : undefined} className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 text-[10px] font-medium ${isActive(item.href) ? 'text-brand-primary' : 'text-[#9a9aac]'}`}>
              {item.icon}
              <span className="max-w-full truncate px-1">{item.label}</span>
            </Link>
          ))}
          <button type="button" onClick={() => setShowCompose(true)} className="flex-1 min-w-0 flex flex-col items-center justify-center gap-1 text-[10px] font-semibold text-brand-primary" aria-label={t('nav.newPost', 'New post')}>
            <span className="w-9 h-9 -mt-3 rounded-full bg-brand-primary text-[#0c0c12] flex items-center justify-center shadow-[0_0_24px_rgba(228,230,58,0.22)]"><Plus size={21} /></span>
            <span className="max-w-full truncate px-1">{t('common.create', 'Create')}</span>
          </button>
          <Link to="/dashboard/chats" aria-current={isActive('/dashboard/chats') ? 'page' : undefined} className={`relative flex-1 min-w-0 flex flex-col items-center justify-center gap-1 text-[10px] font-medium ${isActive('/dashboard/chats') ? 'text-brand-primary' : 'text-[#9a9aac]'}`}>
            <span className="relative"><Mail size={20} />{unreadMessages > 0 && <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-red-500 text-[9px] text-white flex items-center justify-center">{unreadMessages > 9 ? '9+' : unreadMessages}</span>}</span>
            <span className="max-w-full truncate px-1">{t('nav.chats', 'Chats')}</span>
          </Link>
          <button type="button" onClick={() => setSidebarOpen(true)} className="flex-1 min-w-0 flex flex-col items-center justify-center gap-1 text-[10px] font-medium text-[#9a9aac]" aria-label={t('common.more', 'More')}>
            <Menu size={20} />
            <span className="max-w-full truncate px-1 capitalize">{t('common.more', 'More')}</span>
          </button>
        </nav>
      )}

      {/* Compose Post Modal */}
      {showCompose && (
        <div className="fixed inset-0 z-55 flex items-start justify-center pt-[10vh] sm:pt-[12vh]">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={() => setShowCompose(false)} />
          <div className="relative w-full max-w-xl mx-4">
            <FullComposer
              modal
              initialAssets={composeInitialAssets}
              onClose={() => { setShowCompose(false); setComposeInitialAssets(undefined); }}
              onPostSuccess={() => {
                setShowCompose(false);
                setComposeInitialAssets(undefined);
                // In React Router, there's no navigate(0) — we can trigger a re-render or navigate to same path
                if (pathname === '/dashboard' || pathname.startsWith('/dashboard/feed')) {
                  navigate(0);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* AI Assistant */}
      <AIAssistant />

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        commands={commandItems}
      />

      {/* Save to Collection (Ctrl+Shift+S) */}
      <SaveToCollectionModal
        isOpen={showSaveToCollection}
        onClose={() => setShowSaveToCollection(false)}
        content={{
          url: typeof window !== 'undefined' ? window.location.href : '',
          title: typeof document !== 'undefined' ? document.title : '',
        }}
      />

      {/* Username Prompt Modal */}
      {showUsernamePrompt && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-xs" />
          <div className="relative w-full max-w-md bg-[#0a0a12] border border-white/6 rounded-2xl shadow-2xl overflow-hidden">
            <div className="h-1 bg-linear-to-r/srgb from-brand-primary to-brand-secondary" />
            <div className="p-6">
              <div className="text-center mb-5">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brand-primary/10 flex items-center justify-center">
                  <User size={22} className="text-brand-primary" />
                </div>
                <h2 className="text-lg font-semibold text-white">{t('profile.chooseUsername')}</h2>
                <p className="text-sm text-[#9a9aac] mt-1">{t('profile.usernameHint')}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9a9aac] text-sm">@</span>
                    <input
                      type="text"
                      value={usernameInput}
                      onChange={(e) => setUsernameInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      placeholder={t('profile.usernamePlaceholder')}
                      maxLength={30}
                      autoFocus
                      className="w-full pl-8 pr-10 py-2.5 bg-white/3 text-white text-sm border border-white/6 rounded-lg focus:border-brand-primary focus:outline-hidden placeholder:text-[#9a9aac] transition-colors"
                      onKeyDown={(e) => e.key === 'Enter' && usernameStatus.available && handleSetUsername()}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {usernameStatus.checking && <Loader2 size={14} className="text-[#9a9aac] animate-spin" />}
                      {!usernameStatus.checking && usernameStatus.available && <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg></div>}
                      {!usernameStatus.checking && usernameStatus.available === false && <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center"><X size={10} className="text-white" /></div>}
                    </div>
                  </div>
                  {usernameStatus.error && (
                    <p className="text-[11px] text-red-400 mt-1">{usernameStatus.error}</p>
                  )}
                  {usernameStatus.available && (
                    <p className="text-[11px] text-green-400 mt-1">{t('profile.usernameAvailable')}</p>
                  )}
                </div>

                <div className="text-[10px] text-[#828294] space-y-0.5">
                  <p>{t('profile.usernameRules')}</p>
                  <p>{t('profile.usernameMustStartWithLetter')}</p>
                </div>

                <button
                  onClick={handleSetUsername}
                  disabled={!usernameStatus.available || savingUsername}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-brand-ink bg-brand-primary hover:bg-brand-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {savingUsername ? <Loader2 size={14} className="animate-spin" /> : null}
                  {savingUsername ? t('profile.settingUsername') : t('profile.setUsername')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Flow (profile setup + interests, follow, hubs) */}
      {showOnboardingFlow && (
        <OnboardingFlow
          needsProfile={needsProfile}
          onComplete={() => { setShowOnboardingFlow(false); setNeedsProfile(false); sessionStorage.setItem('soltik_onboarding_done', '1'); completeTour(); }}
        />
      )}

      {/* Post overlay — feed stays mounted, post opens as slide-over */}
      {activeScoopId && (
        <ScoopPanel scoopId={activeScoopId} onClose={handleCloseScoopPanel} />
      )}

      {/* Onboarding Tour */}
      <OnboardingTour
        isActive={isTourActive}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={skipTour}
        onComplete={completeTour}
        goToStep={goToStep}
      />
    </div>
    </AIAssistantProvider>
  );
}

export default function DashboardLayout() {
  return (
    <DeviceProvider>
      <DashboardLayoutInner />
    </DeviceProvider>
  );
}
